import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import "../styles/MonthlySummaryReport.css";
import API_URL from "../apiConfig";

export default function MonthlySummaryReport({ token, finYear }) {
  const [summary, setSummary] = useState(null);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return d.toISOString().substring(0, 7); // YYYY-MM
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch summary when user clicks Fetch button
  const fetchSummary = () => {
    if (!month) return;
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/income_expense/monthly_summary?month=${month}&finyear=${finYear}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error("Could not fetch summary");
        return res.json();
      })
      .then(setSummary)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  // Fetch summary on mount and when finYear changes
  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line
  }, [token, finYear]);

  const fmt = n => Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 });

  function formatDate(d) {
    if (!d) return "-";
    if (/^\d{2}-\d{2}-\d{4}$/.test(d)) return d;
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const [y, m, dd] = d.split("-");
      return `${dd}-${m}-${y}`;
    }
    // fallback to JS Date parsing
    const dateObj = new Date(d);
    if (isNaN(dateObj)) return d;
    const dd = String(dateObj.getDate()).padStart(2, "0");
    const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
    const yyyy = dateObj.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }

  const handleDownloadExcel = () => {
    if (!summary || !summary.daily || !summary.daily.length) return;
    const exportData = summary.daily.map(row => ({
      Date: formatDate(row.date),
      "Total Income": Number(row.total_income || 0),
      "Total Expense": Number(row.total_expense || 0),
      Profit: Number(row.profit ?? (row.total_income || 0) - (row.total_expense || 0))
    }));
    exportData.unshift({
      Date: "Opening Balance",
      "Total Income": "",
      "Total Expense": "",
      Profit: "",
      "Opening Balance": summary.opening_balance
    });
    exportData.push({
      Date: "Closing Balance",
      "Total Income": summary.total_income,
      "Total Expense": summary.total_expense,
      Profit: summary.net_profit,
      "Closing Balance": summary.closing_balance
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MonthlySummary");
    XLSX.writeFile(wb, `MonthlySummary_${month}.xlsx`);
  };

  return (
    <div className="ms-container">
      <h2 className="ms-title">Monthly Business Summary</h2>
      <div className="ms-controls">
        <label className="ms-label">Month:</label>
        <input
          type="month"
          value={month}
          onChange={e => setMonth(e.target.value)}
          className="ms-month"
        />
        <button
          onClick={fetchSummary}
          className="ms-btn"
        >
          Fetch
        </button>
        <button
          onClick={handleDownloadExcel}
          disabled={!summary || !summary.daily || !summary.daily.length}
          className="ms-btn"
        >
          Download Excel
        </button>
      </div>
      {loading && <div>Loading...</div>}
      {error && <div className="ms-error">{error}</div>}
      {(summary && summary.daily && summary.daily.length > 0) ? (
        <>
          <div className="ms-summary-box">
            <div><b>Opening Balance:</b> <span className="ms-green">{fmt(summary.opening_balance)}</span></div>
            <div><b>Total Income:</b> <span className="ms-green">{fmt(summary.total_income)}</span></div>
            <div><b>Total Expense:</b> <span className="ms-red">{fmt(summary.total_expense)}</span></div>
            <div>
              <b>Net Profit:</b> <span className={summary.net_profit >= 0 ? "ms-green" : "ms-red"}>
                {fmt(summary.net_profit)}
              </span>
            </div>
            <div><b>Closing Balance:</b> <span className="ms-green">{fmt(summary.closing_balance)}</span></div>
          </div>
          <div className="ms-table-box">
            <table className="ms-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Total Income</th>
                  <th>Total Expense</th>
                  <th>Profit</th>
                </tr>
              </thead>
              <tbody>
                {summary.daily.map((row, i) => (
                  <tr key={i} className={i % 2 === 1 ? "ms-row-alt" : ""}>
                    <td>{formatDate(row.date)}</td>
                    <td className="ms-green">{fmt(row.total_income)}</td>
                    <td className="ms-red">{fmt(row.total_expense)}</td>
                    <td className={row.profit >= 0 ? "ms-green" : "ms-red"}>
                      {fmt(typeof row.profit !== "undefined" ? row.profit : row.total_income - row.total_expense)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (!loading && !error) ? (
        <div className="ms-nodata">No data for this month.</div>
      ) : null}
    </div>
  );
}